/**
 * Partner Onboarding Page (V2 — Contract Package)
 *
 * PUBLIC page - no authentication required. Security via invitation token.
 * Handles B2 partner onboarding flow using IntakeWizardScroll.
 * Users arrive with a token in the URL and complete the 8-step wizard.
 *
 * Issue #484 - B1-to-B2 Partner Invitation & Onboarding Flow
 * Contract-Driven Partner Invitation System
 *
 * Flow:
 * 1. B2 clicks email link with token
 * 2. Page validates token and loads invitation + contract package
 * 3. B2 completes 8-step wizard (company info, contract review, signer assignment, e-signing)
 * 4. System creates business, user, partnership, and signing envelopes
 * 5. B2 is redirected to Business Portal
 */

import BusinessIcon from '@mui/icons-material/Business';
import SuccessIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { Box, GlassButton, IntakeWizardScroll, ShimmerLoader, Typography, useI18n } from '@ops-matrix/design-system-ui';
import { partnerOnboardingTemplate, partnerOnboardingTemplateV2 } from '@ops-matrix/shared-types';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import env from '@/config/env';
import { auth } from '@/config/firebase';
import useRedirect from '@/hooks/useRedirect';

const TERMINAL_INVITATION_STATUSES: ReadonlySet<string> = new Set(['EXPIRED', 'CANCELLED']);

// Contract details from the invitation
interface InvitationContract {
  id: string;
  contract_type: string;
  contract_type_definition_id: string;
  contract_terms?: Record<string, unknown>;
  b1_signer_email?: string;
  b1_signer_name?: string;
  b1_signer_role?: string;
  signing_envelope_id?: string;
  status: string;
  sort_order: number;
  definition?: {
    name: string;
    description?: string;
    icon?: string;
    contract_type: string;
  };
}

/**
 * Partner Onboarding Page Component (V2)
 *
 * Uses IntakeWizardScroll with partnerOnboardingTemplateV2 for the 8-step onboarding.
 * Supports multi-contract review, signer assignment, and sequential e-signing.
 */
export default function PartnerOnboardingPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { redirect: performRedirect } = useRedirect();
  const { t } = useI18n();

  const invitationToken = searchParams.get('token');

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [invitation, setInvitation] = useState<Record<string, unknown> | null>(null);
  const [contracts, setContracts] = useState<InvitationContract[]>([]);

  // Determine if this invitation has contracts (V2) or not (V1)
  const hasContracts = contracts.length > 0;
  const template = hasContracts ? partnerOnboardingTemplateV2 : partnerOnboardingTemplate;

  /**
   * Validate invitation token and load invitation details (with contracts if available)
   */
  useEffect(() => {
    async function validateToken() {
      if (!invitationToken) {
        setError('Missing invitation token. Please check your email link.');
        setIsLoading(false);
        return;
      }

      try {
        // First try the enhanced endpoint that includes contracts
        const contractsResponse = await fetch(
          `${env.VITE_API_URL}/trpc/partnerInvitations.getWithContracts?input=${encodeURIComponent(JSON.stringify({ invitationToken }))}`,
          {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
          },
        );

        if (contractsResponse.ok) {
          const data = await contractsResponse.json();
          if (data.result?.data) {
            // AC14.1: Check if invitation has expired (including mid-wizard expiry)
            const invitationStatus = data.result.data.status as string;
            if (TERMINAL_INVITATION_STATUSES.has(invitationStatus)) {
              throw new Error(
                invitationStatus === 'EXPIRED'
                  ? t('partner.onboarding.invitationExpired')
                  : t('partner.onboarding.invitationCancelled'),
              );
            }
            setInvitation(data.result.data);
            setContracts((data.result.data.contracts as InvitationContract[]) || []);
            setIsLoading(false);
            return;
          }
        }

        // Fallback to basic token validation for V1 invitations
        const response = await fetch(
          `${env.VITE_API_URL}/trpc/partnerInvitations.getByToken?input=${encodeURIComponent(JSON.stringify({ invitationToken }))}`,
          {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
          },
        );

        if (!response.ok) {
          throw new Error('Invalid or expired invitation');
        }

        const data = await response.json();
        if (!data.result?.data) {
          throw new Error('Invalid invitation response');
        }

        // AC14.1: Check if invitation has expired (including mid-wizard expiry)
        const invitationStatus = data.result.data.status as string;
        if (TERMINAL_INVITATION_STATUSES.has(invitationStatus)) {
          throw new Error(
            invitationStatus === 'EXPIRED'
              ? t('partner.onboarding.invitationExpired')
              : t('partner.onboarding.invitationCancelled'),
          );
        }

        setInvitation(data.result.data);
        setIsLoading(false);
      } catch (err) {
        console.error('Failed to validate invitation token:', err);
        setError(err instanceof Error ? err.message : 'Invalid or expired invitation');
        setIsLoading(false);
      }
    }

    validateToken();
  }, [invitationToken, t]);

  /**
   * Build initial values with contract data for the wizard
   */
  const initialValues = useMemo(() => {
    if (!invitation) return {};

    // Split contact name into first/last for admin fields
    const contactName = (invitation.invited_contact_name as string) || '';
    const nameParts = contactName.trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    // Extract contract terms (commission, payment, dates) from invitation
    const terms = (invitation.contract_terms as Record<string, unknown>) || {};

    const values: Record<string, unknown> = {
      // Pre-fill welcome step data — B1 (inviter) business name, enriched by gateway getByToken
      invitingCompanyName:
        (invitation.inviting_business_name as string) ||
        (invitation.created_by_name as string) ||
        t('partner.onboarding.yourPartner'),
      partnerType: invitation.partner_type,
      // Pre-fill company profile from invitation
      companyName: invitation.invited_company_name,
      phone: invitation.invited_phone,
      // Pre-fill admin account from invitation contact
      adminEmail: invitation.invited_email,
      adminFirstName: firstName,
      adminLastName: lastName,
      adminPhone: invitation.invited_phone,
      // Pre-fill commission terms (shown in Welcome preview + Review Terms)
      commissionType: terms.commission_type,
      commissionRate: terms.commission_rate,
      commissionTrigger: terms.commission_trigger,
      paymentTerms: terms.payment_schedule,
      // Pre-fill agreement terms (shown in Review Terms)
      effectiveDate: terms.effective_date,
      expirationDate: terms.expiration_date,
      exclusivityScope: terms.exclusivity_scope,
      autoRenew: terms.renewal_terms === 'AUTO_RENEW',
      partnerVisibility: terms.partner_visibility,
    };

    if (hasContracts) {
      // Provide contract package overview for welcome step
      values.contractCount = `${contracts.length} contract(s)`;
      values.contractList = contracts.map((c) => c.definition?.name || c.contract_type).join(', ');
    }

    return values;
  }, [invitation, hasContracts, contracts]);

  /**
   * Assign B2 signers for all contracts
   */
  const assignSigners = useCallback(
    async (data: Record<string, unknown>) => {
      if (!invitationToken || !hasContracts) return;

      const assignments = contracts.map((contract) => {
        // Check for per-contract signer overrides, fallback to admin details
        const email = (data[`b2Signer_${contract.contract_type}_email`] as string) || (data.adminEmail as string);
        const name =
          (data[`b2Signer_${contract.contract_type}_name`] as string) ||
          `${data.adminFirstName || ''} ${data.adminLastName || ''}`.trim();
        const role = (data[`b2Signer_${contract.contract_type}_role`] as string) || 'Admin';

        return {
          contractId: contract.id,
          email,
          name,
          role,
        };
      });

      await fetch(`${env.VITE_API_URL}/trpc/partnerInvitations.assignB2Signers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invitationToken, assignments }),
      });
    },
    [invitationToken, hasContracts, contracts],
  );

  /**
   * Handle wizard submission
   * Completes onboarding: creates business, user, partnership, and signing envelopes
   */
  const handleSubmit = async (data: Record<string, unknown>) => {
    if (!invitationToken) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Assign B2 signers before completing onboarding
      await assignSigners(data);

      // Prepare complete onboarding payload
      const completePayload: Record<string, unknown> = {
        invitationToken,
        realmId: invitation?.business_id,
        companyName: data.companyName,
        nmlsCompanyId: data.nmlsCompanyId,
        address: {
          street: data.addressStreet,
          city: data.addressCity,
          state: data.addressState,
          zip: data.addressZip,
        },
        phone: data.phone,
        website: data.website,
        adminEmail: data.adminEmail,
        adminFirstName: data.adminFirstName,
        adminLastName: data.adminLastName,
        adminPassword: data.password,
        licenseDocuments: Array.isArray(data.licenseDocuments)
          ? (data.licenseDocuments as Array<Record<string, unknown>>).map((doc) => ({
              type: doc.type,
              url: doc.url,
              filename: doc.filename,
              uploadedAt: new Date().toISOString(),
            }))
          : [],
      };

      // Include contract review data if this is a V2 invitation
      if (hasContracts) {
        completePayload.contractReviewData = {
          reviewedAt: new Date().toISOString(),
          contractCount: contracts.length,
        };
      }

      // Call complete onboarding API
      const response = await fetch(`${env.VITE_API_URL}/trpc/partnerInvitations.completeOnboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(completePayload),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        const serverMessage =
          errorBody?.error?.message ||
          errorBody?.error?.data?.zodError?.formErrors?.[0] ||
          t('partner.onboarding.completeFailed');
        throw new Error(serverMessage);
      }

      // Auto-login the newly-created B2 admin so they land on the business
      // portal already authenticated instead of dropping on /login. The
      // 8-step wizard already collected adminEmail + password and the
      // backend just provisioned the Firebase user — re-using those creds
      // here is the same security posture as a regular sign-in. We hold
      // setSuccess(true) until either the auto-login path completes (in
      // which case we navigate away before the success screen needs to
      // render) OR the auto-login fails and we want the success screen
      // visible while the fallback /login redirect runs.
      const adminEmail = data.adminEmail as string | undefined;
      const adminPassword = data.password as string | undefined;
      // Snapshot the password into a local ref then clear it from `data`
      // so it doesn't linger in component state for the lifetime of the
      // wizard (cursor #3205655391: in-memory password lifetime).
      let scopedPassword: string | undefined = adminPassword;
      try {
        delete (data as Record<string, unknown>).password;
      } catch {
        // data is plain object — defensive in case a Proxy is passed.
      }
      if (adminEmail && scopedPassword) {
        try {
          const userCredential = await signInWithEmailAndPassword(auth, adminEmail, scopedPassword);
          // Clear the local copy as soon as Firebase has accepted it; we
          // don't need the cleartext password again on this code path.
          scopedPassword = undefined;
          const idToken = await userCredential.user.getIdToken();
          const exchangeResp = await fetch(`${env.VITE_API_URL}/auth/exchange`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ firebaseIdToken: idToken }),
          });
          if (exchangeResp.ok) {
            const { accessToken } = (await exchangeResp.json()) as { accessToken: string };
            await performRedirect(accessToken);
            return;
          }
        } catch (autoLoginErr) {
          // Auto-login is a UX optimization — if it fails (Firebase replication
          // race, network blip, etc.) fall through to the manual-login path
          // below. The user has working credentials they can use directly.
          // Log a sanitized message rather than the raw error object: Firebase
          // auth errors can carry the email + a leaked password fragment in
          // their `customData` (cursor #3204104471).
          const safeMessage = autoLoginErr instanceof Error ? autoLoginErr.name : 'unknown';
          console.warn('[PartnerOnboarding] auto-login failed (%s), falling back to /login', safeMessage);
        } finally {
          scopedPassword = undefined;
        }
      }

      // Auto-login didn't redirect — show the success screen while the
      // /login redirect timer runs.
      setSuccess(true);
      setTimeout(() => {
        navigate('/login?message=onboarding-complete');
      }, 3000);
    } catch (err) {
      console.error('Failed to complete partner onboarding:', err);
      setError(err instanceof Error ? err.message : t('partner.onboarding.completeFailed'));
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 3,
        }}
      >
        <ShimmerLoader variant="card" />
        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
          {t('partner.onboarding.validating')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 4,
          px: 3,
        }}
      >
        <ErrorIcon sx={{ fontSize: 80, color: 'error.main' }} />
        <Typography variant="h4" sx={{ textAlign: 'center' }}>
          {t('partner.onboarding.invalidInvitation')}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 600 }}>
          {error}
        </Typography>
        <GlassButton variant="outlined" onClick={() => navigate('/login')}>
          {t('partner.onboarding.goToLogin')}
        </GlassButton>
      </Box>
    );
  }

  if (success) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 4,
          px: 3,
        }}
      >
        <SuccessIcon sx={{ fontSize: 80, color: 'success.main' }} />
        <Typography variant="h4" sx={{ textAlign: 'center' }}>
          {t('partner.onboarding.complete')}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 600 }}>
          {hasContracts
            ? t('partner.onboarding.activatedWithContracts', { count: contracts.length })
            : t('partner.onboarding.activated')}
        </Typography>
      </Box>
    );
  }

  if (isSubmitting) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 3,
        }}
      >
        <ShimmerLoader variant="card" />
        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
          {t('partner.onboarding.creatingAccount')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{ px: 4, py: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BusinessIcon sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5">{t('auth.partnerOnboarding.title')}</Typography>
            <Typography variant="body2" color="textSecondary">
              {hasContracts
                ? t('auth.partnerOnboarding.invitedByWithContracts', {
                    companyName:
                      (invitation?.inviting_business_name as string) || (invitation?.invited_company_name as string),
                    count: contracts.length,
                  })
                : t('auth.partnerOnboarding.invitedBy', {
                    companyName:
                      (invitation?.inviting_business_name as string) || (invitation?.invited_company_name as string),
                  })}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Wizard */}
      <Box sx={{ px: 4, py: 4 }}>
        <IntakeWizardScroll
          template={template}
          initialValues={initialValues}
          onComplete={handleSubmit}
          onCancel={() => navigate('/login')}
        />
      </Box>
    </Box>
  );
}
